import React, { useState, useEffect, useRef } from 'react'
import { useQuery, gql, useMutation, useSubscription } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import DataTable from 'react-data-table-component'
import { Box, Modal, Typography, Grid, Card, CardContent, Container, Chip, Paper, Select, MenuItem, FormControl, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, InputLabel } from '@mui/material'
import { format, isToday, isYesterday } from 'date-fns'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableCustomStyles'
import SearchBar from '../components/TableHeader/SearchBar'
import TableHeader from '../components/TableHeader'
import { getDeliveryJobs, updateDeliveryJobStatus, placeDeliveryJobOrder, saveDeliveryJobQuotation, getRidersByZone, assignRiderToDeliveryJob, unassignRiderFromDeliveryJob, riderLocation, subscriptionRiderLocation, subscriptionDeliveryJob } from '../apollo'
import PodPhotos from '../components/PodPhotos/PodPhotos'
import {  LocalShipping, Person,   Phone, LocationOn, 
  Info, ShoppingCart, Schedule, Store, DirectionsCar, Route, 
  LocationCity, TwoWheeler, CheckCircle, Loop
} from '@mui/icons-material'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import DeliveryGroupMap from '../components/Maps/DeliveryGroupMap'

const GET_DELIVERY_JOBS = gql`
  ${getDeliveryJobs}
`

const UPDATE_DELIVERY_JOB_STATUS = gql`
  ${updateDeliveryJobStatus}
`

const PLACE_DELIVERY_JOB_ORDER = gql`
  ${placeDeliveryJobOrder}
`

const SAVE_QUOTATION = gql`
  ${saveDeliveryJobQuotation}
`

const GET_RIDERS_BY_ZONE = gql`
  ${getRidersByZone}
`

const ASSIGN_RIDER_TO_DELIVERY_JOB = gql`
  ${assignRiderToDeliveryJob}
`

const UNASSIGN_RIDER_FROM_DELIVERY_GROUP = gql`
  ${unassignRiderFromDeliveryJob}
`

const RIDER_LOCATION = gql`
  ${riderLocation}
`

const SUBSCRIPTION_RIDER_LOCATION = gql`
  ${subscriptionRiderLocation}
`

const SUBSCRIPTION_DELIVERY_JOB = gql`
  ${subscriptionDeliveryJob}
`

// Add these style objects at the top of the component
const styles = {
  modal: {
    width: '75%',
    marginLeft: '13%',
    overflowY: 'auto'
  },
  paper: {
    bgcolor: '#f8f9fa',
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: 3,
    '& .MuiTypography-root': {
      color: '#2d3436'
    },
    '& .MuiListItemText-primary': {
      color: '#2d3436'
    },
    '& .MuiListItemText-secondary': {
      color: '#636e72'
    }
  },
  sectionTitle: {
    color: '#2d3436',
    borderBottom: '2px solid #00b894',
    pb: 1,
    mb: 3
  },
  card: {
    mb: 2, 
    bgcolor: 'white',
    boxShadow: 2,
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem',
      mr: 1,
      color: '#00b894'
    },
    '&:hover': {
      boxShadow: 3,
      transition: 'all 0.3s ease'
    }
  },
  orderCard: {
    mb: 2, 
    bgcolor: 'white',
    boxShadow: 2,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      mr: 1,
      color: '#00b894'
    },
    '&:hover': {
      boxShadow: 3,
      transition: 'all 0.3s ease'
    }
  },
  orderHeader: {
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    mb: 2,
    pb: 2,
    borderBottom: '1px solid #e0e0e0'
  },
  select: {
    '& .MuiSelect-select': {
      py: 0.5,
      pl: 1,
      color: '#000000'
    }
  },
  menuItem: {
    color: '#000000'
  },
  formControl: {
    minWidth: 150,
    ml: 2
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 3
  },
  selectContainer: {
    minWidth: 120
  }
}

// Map DeliveryJob from API to the shape the UI expects (legacy delivery group shape)
function mapDeliveryJobToView(job) {
  const pickupStop = job.stops?.find(s => s.type === 'PICKUP')
  const restaurant = pickupStop ? {
    _id: null,
    name: pickupStop.restaurantName || 'Restaurant',
    phone: pickupStop.restaurantPhone || '',
    image: null,
    address: pickupStop.address || '',
    addressDetails: pickupStop.details || '',
    addressInstruction: pickupStop.deliveryInstructions || '',
    location: pickupStop.location ? { coordinates: pickupStop.location.coordinates } : null
  } : null
  const totalAddresses = job.stops?.filter(s => s.type === 'DROPOFF').length ?? 0
  return {
    _id: job.id,
    deliveryJobId: job.groupId,
    timezone: job.timezone ?? null,
    restaurant,
    zone: job.zone ?? null,
    scheduledPickupTime: job.pickupTime,
    orderIds: job.orders?.map(order => order.orderId) ?? [],
    orders: [],
    stops: (job.stops || []).map(s => ({
      ...s,
      orderId: s.orders?.map(o => o.orderId).filter(Boolean).join(', ') ?? null,
      orderStatus: s.orders?.[0]?.orderStatus ?? s.orderStatus ?? null
    })),
    status: job.status,
    distanceKmTotal: job.distance,
    totalAddresses,
    vehicleType: job.vehicleType,
    riderId: job.riderId ?? null,
    riderName: job.riderName ?? null,
    riderPhone: job.riderPhone ?? null,
    deliveryWage: job.deliveryWage,
    lalamoveDeliveryFee: job.lalamoveDeliveryFee ?? null
  }
}

// Rider Assignment Select Component
function RiderAssignmentSelect({ zoneId, selectedRiderId, selectedRiderName, onAssign, onUnassign }) {
  const { data: dataZone, loading: loadingRiders } = useQuery(GET_RIDERS_BY_ZONE, {
    variables: { id: zoneId },
    skip: !zoneId
  })

  // Find the rider name from the list if available, otherwise use the passed selectedRiderName
  const currentRiderName = selectedRiderId && dataZone?.ridersByZone 
    ? dataZone.ridersByZone.find(r => r._id === selectedRiderId)?.name || selectedRiderName || 'Assigned Rider'
    : selectedRiderName || ''

  return (
    <FormControl size="small" sx={styles.formControl}>
      <Select
        id="input-rider"
        name="input-rider"
        value={selectedRiderId || ''}
        displayEmpty
        inputProps={{ 'aria-label': 'Without label' }}
        sx={{
          ...styles.select,
          '& .MuiSelect-select': {
            py: 0.5,
            pl: 1,
            color: '#000000'
          }
        }}
        renderValue={(value) => {
          if (!value) return 'Select Rider'
          return currentRiderName || 'Assigned Rider'
        }}
      >
        {selectedRiderId && onUnassign ? (
          // When rider is assigned, only show unassign option
          <MenuItem
            value="__unassign__"
            onClick={() => {
              onUnassign()
            }}
            sx={{ ...styles.menuItem, color: '#d32f2f', fontWeight: 'medium' }}
          >
            Unassign Rider
          </MenuItem>
        ) : (
          // When no rider is assigned, show list of available riders
          <>
            {loadingRiders && (
              <MenuItem value="" disabled sx={styles.menuItem}>
                Loading riders...
              </MenuItem>
            )}
            {!loadingRiders && (!dataZone || !dataZone.ridersByZone || dataZone.ridersByZone.length === 0) && (
              <MenuItem value="" disabled sx={styles.menuItem}>
                No riders available
              </MenuItem>
            )}
            {dataZone &&
              dataZone.ridersByZone &&
              dataZone.ridersByZone.map(rider => (
                <MenuItem
                  style={{ color: 'black' }}
                  onClick={() => {
                    onAssign(rider._id, rider.name)
                  }}
                  key={rider._id}
                  value={rider._id}
                  sx={styles.menuItem}
                >
                  {rider.name}
                </MenuItem>
              ))}
          </>
        )}
      </Select>
    </FormControl>
  )
}

function DeliveryGroups({ t }) {
  const globalClasses = useGlobalStyles()
  const [jobs, setJobs] = useState([])
  const [subscriptionZoneId, setSubscriptionZoneId] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const [createOrderPerPickup, setCreateOrderPerPickup] = useState(false)
  const [isSavingQuotation, setIsSavingQuotation] = useState(false)
  const [assignRiderDialogOpen, setAssignRiderDialogOpen] = useState(false)
  const [unassignRiderDialogOpen, setUnassignRiderDialogOpen] = useState(false)
  const [pendingRiderId, setPendingRiderId] = useState(null)
  const [pendingRiderName, setPendingRiderName] = useState(null)
  const [riderLocationData, setRiderLocationData] = useState(null)
  const [paginationTotalRows, setPaginationTotalRows] = useState(0)
  /** When set, we know the exact row count (API does not return totalCount yet). */
  const exactDeliveryJobsTotalRef = useRef(null)

  const { loading, error, data, refetch } = useQuery(GET_DELIVERY_JOBS, {
    variables: {
      page: page - 1,
      rows: rowsPerPage,
      search: searchQuery && searchQuery.trim().length > 0 ? searchQuery.trim() : null
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false // Don't show loading state on background refetches
  })

  useEffect(() => {
    setPage(1)
    exactDeliveryJobsTotalRef.current = null
  }, [searchQuery])

  const handleChangeRowsPerPage = newSize => {
    setRowsPerPage(newSize)
    setPage(1)
    exactDeliveryJobsTotalRef.current = null
  }

  useEffect(() => {
    if (loading && !data?.getDeliveryJobs) return
    if (!data?.getDeliveryJobs) {
      if (!loading) setPaginationTotalRows(0)
      return
    }
    if (loading) return

    const list = data.getDeliveryJobs
    const len = list.length
    const offset = (page - 1) * rowsPerPage

    if (len === 0) {
      if (page > 1) {
        const exact = (page - 1) * rowsPerPage
        exactDeliveryJobsTotalRef.current = exact
        setPaginationTotalRows(exact)
        setPage(p => Math.max(1, p - 1))
      } else {
        exactDeliveryJobsTotalRef.current = 0
        setPaginationTotalRows(0)
      }
      return
    }

    if (len < rowsPerPage) {
      const exact = offset + len
      exactDeliveryJobsTotalRef.current = exact
      setPaginationTotalRows(exact)
      return
    }

    const exactKnown = exactDeliveryJobsTotalRef.current
    if (exactKnown != null && exactKnown >= offset + len) {
      setPaginationTotalRows(exactKnown)
      return
    }

    exactDeliveryJobsTotalRef.current = null
    setPaginationTotalRows(offset + len + 1)
  }, [data?.getDeliveryJobs, loading, page, rowsPerPage])

  const [updateStatus] = useMutation(UPDATE_DELIVERY_JOB_STATUS, {
    onCompleted: () => {
      refetch()
      NotificationManager.success(
        t('statusUpdatedSuccessfully'),
        t('Success')
      )
    },
    onError: error => {
      console.error('Error updating status:', error)
      NotificationManager.error(
        t('errorUpdatingStatus'),
        t('Error')
      )
    }
  })

  const [placeDeliveryOrder] = useMutation(PLACE_DELIVERY_JOB_ORDER, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      console.error('Error placing delivery order:', error)
    }
  })

  const [saveQuotation] = useMutation(SAVE_QUOTATION, {
    onCompleted: (data) => {
      NotificationManager.success(
        t('quotationGetSuccessfully') + ` - Lalamove Delivery Fee: $${data.saveDeliveryJobQuotation.lalamoveDeliveryFee}`,
        t('Success')
      )
      if (data?.saveDeliveryJobQuotation?.deliveryWage || data?.saveDeliveryJobQuotation?.lalamoveDeliveryFee) {
        setSelectedGroup(prev => ({
          ...prev,
          deliveryWage: data.saveDeliveryJobQuotation.deliveryWage ?? prev?.deliveryWage,
          lalamoveDeliveryFee: data.saveDeliveryJobQuotation.lalamoveDeliveryFee ?? prev?.lalamoveDeliveryFee
        }))
      }
    },
    onError: error => {
      console.error('Error getting quotation:', error)
      NotificationManager.error(
        t('errorGettingQuotation'),
        t('Error')
      )
    }
  })

  const [assignRider] = useMutation(ASSIGN_RIDER_TO_DELIVERY_JOB, {
    onCompleted: (data) => {
      refetch()
      if (data?.assignRiderToDeliveryJob) {
        setSelectedGroup(prev => ({
          ...prev,
          riderId: data.assignRiderToDeliveryJob.riderId,
          riderName: data.assignRiderToDeliveryJob.riderName,
          riderPhone: data.assignRiderToDeliveryJob.riderPhone,
          status: data.assignRiderToDeliveryJob.status
        }))
      }
      setAssignRiderDialogOpen(false)
      setPendingRiderId(null)
      setPendingRiderName(null)
      NotificationManager.success(
        'Rider assigned successfully!',
        'Success'
      )
    },
    onError: error => {
      console.error('Error assigning rider:', error)
      NotificationManager.error(
        'Failed to assign rider!',
        'Error'
      )
    }
  })

  const [unassignRider] = useMutation(UNASSIGN_RIDER_FROM_DELIVERY_GROUP, {
    onCompleted: (data) => {
      refetch()
      if (data?.unassignRiderFromDeliveryJob) {
        setSelectedGroup(prev => ({
          ...prev,
          riderId: data.unassignRiderFromDeliveryJob.riderId,
          riderName: data.unassignRiderFromDeliveryJob.riderName,
          riderPhone: data.unassignRiderFromDeliveryJob.riderPhone,
          status: data.unassignRiderFromDeliveryJob.status
        }))
      }
      setUnassignRiderDialogOpen(false)
      NotificationManager.success(
        'Rider unassigned successfully!',
        'Success'
      )
    },
    onError: error => {
      console.error('Error unassigning rider:', error)
      NotificationManager.error(
        'Failed to unassign rider!',
        'Error'
      )
    }
  })

  // Initialize jobs list from initial query and derive subscription zone
  useEffect(() => {
    if (data?.getDeliveryJobs) {
      const mappedJobs = data.getDeliveryJobs.map(mapDeliveryJobToView)
      setJobs(mappedJobs)

      if (!subscriptionZoneId) {
        const firstZoneId = data.getDeliveryJobs[0]?.zone?._id
        if (firstZoneId) {
          setSubscriptionZoneId(firstZoneId)
        }
      }
    }
  }, [data, subscriptionZoneId])

  const handleConfirmAssignRider = () => {
    if (pendingRiderId && selectedGroup) {
      assignRider({
        variables: {
          deliveryJobId: selectedGroup._id,
          riderId: pendingRiderId
        }
      })
    }
  }

  const handleConfirmUnassignRider = () => {
    if (selectedGroup) {
      unassignRider({
        variables: {
          deliveryJobId: selectedGroup._id
        }
      })
    }
  }

  const handleRequestAssignRider = (riderId, riderName) => {
    setPendingRiderId(riderId)
    setPendingRiderName(riderName)
    setAssignRiderDialogOpen(true)
  }

  const handleRequestUnassignRider = () => {
    setUnassignRiderDialogOpen(true)
  }

  const handleRowClick = row => {
    setSelectedGroup(row)
    setModalOpen(true)
  }

  const handleStatusChange = async(groupId, newStatus) => {
    try {
      await updateStatus({
        variables: {
          id: groupId,
          status: newStatus
        }
      })
      setSelectedGroup(prev => ({
        ...prev,
        status: newStatus
      }))
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const handlePlaceDelivery = async(groupId) => {
    try {
      setIsPlacingOrder(true)
      const { data } = await placeDeliveryOrder({
        variables: {
          id: groupId,
          createOrderPerPickup
        }
      })
      if (!data.placeDeliveryJobOrder?.id) {
        NotificationManager.error(
          t('errorPlacingDeliveryOrder'),
          t('Error')
        )
      } else {
        setConfirmDialogOpen(false)
        setModalOpen(false)
        setCreateOrderPerPickup(false)
        NotificationManager.success(
          t('deliveryOrderPlacedSuccessfully'),
          t('Success')
        )
      }
    } catch (error) {
      console.error('Failed to place delivery order:', error)
      NotificationManager.error(
        t('errorPlacingDeliveryOrder'),
        t('Error')
      )
    } finally {
      setIsPlacingOrder(false)
    }
  }

  const handleSaveQuotation = async(groupId) => {
    try {
      setIsSavingQuotation(true)
      await saveQuotation({
        variables: {
          id: groupId
        }
      })
    } catch (error) {
      console.error('Failed to get quotation:', error)
    } finally {
      setIsSavingQuotation(false)
    }
  }

  const parseDateValue = value => {
    if (value == null || value === '') return null
    const numeric = Number(value)
    const date = Number.isNaN(numeric) ? new Date(value) : new Date(numeric)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const formatEstimatedTime = (estimatedTime, timezone) => {
    const date = parseDateValue(estimatedTime)
    if (!date) return estimatedTime

    const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    const timeZone = timezone || fallbackTimeZone

    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        day: 'numeric',
        month: 'short',
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).formatToParts(date)

      const getPart = type => parts.find(part => part.type === type)?.value || ''
      const day = getPart('day')
      const month = getPart('month')
      const weekday = getPart('weekday')
      const hour = getPart('hour')
      const minute = getPart('minute')
      const dayPeriod = getPart('dayPeriod').toLowerCase()

      const tzParts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset'
      }).formatToParts(date)
      const timeZoneName = tzParts.find(part => part.type === 'timeZoneName')?.value || 'GMT'

      return `${day}-${month} (${weekday}) ${hour}:${minute} ${dayPeriod} ${timeZoneName}`
    } catch (error) {
      const local = format(date, 'd-MMM (eee) h:mm aaa').replace('AM', 'am').replace('PM', 'pm')
      return `${local} GMT`
    }
  }

  // Determine if we should track rider location
  const shouldTrackLocation = modalOpen && 
    selectedGroup?.riderId && 
    ['ASSIGNED', 'AWAITING_RIDER', 'IN_PROGRESS'].includes(selectedGroup?.status)

  // Fetch initial rider location
  const { data: riderLocationQueryData } = useQuery(RIDER_LOCATION, {
    variables: { id: selectedGroup?.riderId },
    skip: !shouldTrackLocation || !selectedGroup?.riderId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.riderLocation) {
        setRiderLocationData(data.riderLocation)
      }
    },
    onError: (error) => {
      console.error('Error fetching rider location:', error)
    }
  })

  // Subscribe to rider location updates using useSubscription
  useSubscription(SUBSCRIPTION_RIDER_LOCATION, {
    variables: { riderId: selectedGroup?.riderId },
    skip: !shouldTrackLocation || !selectedGroup?.riderId,
    onData: ({ data: subscriptionData }) => {
      if (!subscriptionData?.data) return
      
      const rider = subscriptionData.data.subscriptionRiderLocation
      if (rider?.location) {
        const updatedLocation = {
          _id: rider._id,
          location: rider.location,
          locationUpdatedAt: rider.updatedAt
        }
        // Update state immediately when subscription data arrives
        // This ensures the map marker updates in real-time
        setRiderLocationData(prevLocation => {
          // Only update if coordinates actually changed to avoid unnecessary re-renders
          const prevCoords = prevLocation?.location?.coordinates
          const newCoords = updatedLocation.location.coordinates
          if (prevCoords && newCoords && 
              prevCoords[0] === newCoords[0] && 
              prevCoords[1] === newCoords[1]) {
            return prevLocation
          }
          return updatedLocation
        })
      }
    },
    onError: (error) => {
      console.error('Error in rider location subscription:', error)
    }
  })

  // Update location data when query data changes (for initial load)
  useEffect(() => {
    if (riderLocationQueryData?.riderLocation && shouldTrackLocation) {
      setRiderLocationData(riderLocationQueryData.riderLocation)
    }
  }, [riderLocationQueryData, shouldTrackLocation])

  // Clean up location data when we shouldn't track location
  useEffect(() => {
    if (!shouldTrackLocation) {
      setRiderLocationData(null)
    }
  }, [shouldTrackLocation])

  // Subscribe to delivery job updates for the active zone
  useSubscription(SUBSCRIPTION_DELIVERY_JOB, {
    variables: { zoneId: subscriptionZoneId },
    skip: !subscriptionZoneId,
    onData: ({ data: subscriptionData }) => {
      if (!subscriptionData?.data?.subscriptionDeliveryJob) return

      const { deliveryJob } = subscriptionData.data.subscriptionDeliveryJob
      if (!deliveryJob) return

      const mappedJob = mapDeliveryJobToView(deliveryJob)

      setJobs(prevJobs => {
        const index = prevJobs.findIndex(job => job._id === mappedJob._id)
        if (index === -1) {
          // Add new job if it does not exist
          return [mappedJob, ...prevJobs]
        }

        // Update existing job, do not remove from the list
        const updatedJobs = [...prevJobs]
        updatedJobs[index] = { ...updatedJobs[index], ...mappedJob }
        return updatedJobs
      })

      // Keep modal selection in sync if it's the same job
      setSelectedGroup(prev => {
        if (!prev || prev._id !== mappedJob._id) return prev
        return { ...prev, ...mappedJob }
      })
    },
    onError: (error) => {
      console.error('Error in delivery job subscription:', error)
    }
  })

  const columns = [
    {
      name: t('pickUp'),
      selector: row => row.restaurant?.name ?? '-',
      sortable: true
    },
    {
      name: t('PickupTime'),
      cell: row => row.scheduledPickupTime ? 
        format(new Date(row.scheduledPickupTime), 'd-MMM (eee) h:mm aaa') : '-',
      sortable: true
    },{
      name: t('Addresses'),
      selector: row => row.totalAddresses,
      sortable: true
    },
    {
      name: t('totalItems'),
      cell: row => {
        if (row.stops?.length) {
          return row.stops.reduce((sum, s) => sum + (s.itemCount ?? s.totalPackages ?? 0), 0)
        }
        return row.orders?.reduce((sum, order) => {
          return sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0)
        }, 0) ?? 0
      },
      sortable: true
    },
    {
      name: t('Vehicle'),
      selector: row => row.vehicleType,
      sortable: true
    },
    {
      name: t('Status'),
      cell: row => {
        //   if (row.status === 'COMPLETED') return <Chip label="COMPLETED" color="#4CAF50" size="small" />
        //   if (row.status === 'CANCELLED') return <Chip label="CANCELLED" color="#F44336" size="small" />
        //   if (row.status === 'IN_PROGRESS') return <Chip label="IN_PROGRESS" color="#2196F3" size="small" />
        //   if (row.status === 'ASSIGNED') return <Chip label="ASSIGNED" color="#9C27B0" size="small" />
        //   if (row.status === 'FINDING_RIDER') return <Chip label="FINDING_RIDER" color="#00BCD4" size="small" />
        // return <Chip label="PENDING" color="warning" size="small" />
        return <Chip 
            label={row.status} 
            sx={{ 
              ml: 2,
              backgroundColor: 
              row.status === 'COMPLETED' ? '#4CAF50' :
              row.status === 'CANCELLED' ? '#F44336' :
              row.status === 'IN_PROGRESS' ? '#2196F3' :
              row.status === 'ASSIGNED' ? '#9C27B0' :
              row.status === 'FINDING_RIDER' ? '#00BCD4' :
                '#FF9800',  // PENDING
              color: '#fff'  // white text for all statuses
            }}
            size="small"
        />
      },
      sortable: true
    },
  ]

  // Server-side pagination and search via getDeliveryJobs(page, rows, search)
  const filtered = jobs

  // Helper function to get jobId - delivery job id is used for mutations and PodPhotos
  const getJobId = (jobOrGroupId) => {
    return jobOrGroupId
  }

  if (error) return `Error! ${error.message}`

  return (
    <>
      <NotificationContainer />
      <Header />
      <Container className={globalClasses.flex} fluid>
        <DataTable
          title={<TableHeader title="Delivery Jobs" />}
          columns={columns}
          data={filtered || []}
          progressPending={loading}
          progressComponent={<CustomLoader />}
          pagination
          paginationServer
          paginationTotalRows={paginationTotalRows}
          paginationPerPage={rowsPerPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onChangePage={setPage}
          subHeader
          subHeaderComponent={
            <SearchBar
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClick={() => {
                setPage(1)
                exactDeliveryJobsTotalRef.current = null
                refetch()
              }}
            />
          }
          onRowClicked={handleRowClick}
          pointerOnHover
          highlightOnHover
          customStyles={customStyles}
        />

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          aria-labelledby="delivery-group-modal"
          style={styles.modal}
        >
          <Paper sx={styles.paper}>
            {selectedGroup && (
              <>
                <Box sx={styles.headerContainer}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ ...styles.sectionTitle, mb: 0 }}>
                      {t('Pickup')} - {selectedGroup.deliveryJobId}
                    </Typography>
                    {selectedGroup && (
                      <Chip 
                        label={selectedGroup.status} 
                        sx={{ 
                          ml: 2,
                          backgroundColor: 
                            selectedGroup.status === 'COMPLETED' ? '#4CAF50' :
                            selectedGroup.status === 'CANCELLED' ? '#F44336' :
                            selectedGroup.status === 'IN_PROGRESS' ? '#2196F3' :
                            selectedGroup.status === 'ASSIGNED' ? '#9C27B0' :
                            selectedGroup.status === 'FINDING_RIDER' ? '#00BCD4' :
                            '#FF9800',  // PENDING
                          color: '#fff'  // white text for all statuses
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {selectedGroup && !['COMPLETED'].includes(selectedGroup.status) && (
                      <>
                        <FormControl size="small" sx={styles.formControl}>
                          <Select
                            value={selectedGroup.status}
                            onChange={(e) => handleStatusChange(selectedGroup._id, e.target.value)}
                            sx={{
                              ...styles.select,
                              '& .MuiSelect-select': {
                                py: 0.5,
                                pl: 1,
                                color: '#000000'
                              }
                            }}
                            displayEmpty
                          >
                            <MenuItem value="PENDING" sx={styles.menuItem}>PENDING</MenuItem>
                            <MenuItem value="FINDING_RIDER" sx={styles.menuItem}>FINDING_RIDER</MenuItem>
                            <MenuItem value="ASSIGNED" sx={styles.menuItem}>ASSIGNED</MenuItem>
                            <MenuItem value="IN_PROGRESS" sx={styles.menuItem}>IN_PROGRESS</MenuItem>
                            <MenuItem value="COMPLETED" sx={styles.menuItem}>COMPLETED</MenuItem>
                            <MenuItem value="CANCELLED" sx={styles.menuItem}>CANCELLED</MenuItem>
                          </Select>
                        </FormControl>
                        {selectedGroup.zone?._id && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#000000', whiteSpace: 'nowrap' }}>
                              Rider:
                            </Typography>
                            <RiderAssignmentSelect 
                              zoneId={selectedGroup.zone._id}
                              selectedRiderId={selectedGroup.riderId}
                              selectedRiderName={selectedGroup.riderName}
                              onAssign={handleRequestAssignRider}
                              onUnassign={handleRequestUnassignRider}
                            />
                          </Box>
                        )}
                      </>
                    )}
                    
                    {selectedGroup && ['PENDING', 'COMPLETED', 'CANCELLED'].includes(selectedGroup.status) && (
                      <>
                        <Button 
                          variant="outlined"
                          color="primary"
                          onClick={() => handleSaveQuotation(selectedGroup._id)}
                          disabled={isSavingQuotation}
                          sx={{ mr: 1 }}
                        >
                          {isSavingQuotation ? t('getting') : t('getQuotation')}
                        </Button>
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={() => setConfirmDialogOpen(true)}
                        >
                          {t('callDelivery')}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
                
                <Card sx={styles.card}>
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Store /> {selectedGroup.restaurant?.name ?? 'Restaurant'}
                        </Typography>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Phone /> {selectedGroup.restaurant?.phone || 'No phone number'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} container spacing={2}>
                        {selectedGroup.distanceKmTotal && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Route /> {selectedGroup.distanceKmTotal.toFixed(0)} km
                          </Typography>
                        </Grid>
                        )}
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <ShoppingCart /> {selectedGroup.stops?.length
                              ? selectedGroup.stops.reduce((sum, s) => sum + (s.itemCount ?? s.totalPackages ?? 0), 0)
                              : selectedGroup.orders?.reduce((sum, order) =>
                                  sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0)} items
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <DirectionsCar /> {selectedGroup.vehicleType}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOn /> {selectedGroup.restaurant?.address ?? ''}
                          </Typography>
                          {selectedGroup.restaurant?.addressDetails && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                              <LocationCity /> {selectedGroup.restaurant.addressDetails}
                            </Typography>
                          )}
                          {selectedGroup.restaurant?.addressInstruction && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                              <Info /> {selectedGroup.restaurant.addressInstruction}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Pickup Stop Photos */}
                {selectedGroup.stops && (() => {
                  const pickupStop = selectedGroup.stops.find(s => s.type === 'PICKUP')
                  const jobId = getJobId(selectedGroup._id)
                  if (pickupStop && jobId && pickupStop.stopId) {
                    return (
                      <>
                        <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
                          Pickup - Proof of Delivery
                        </Typography>
                        <Card sx={styles.card}>
                          <CardContent>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <Store /> {pickupStop.restaurantName || selectedGroup.restaurant?.name}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <LocationOn /> {pickupStop.address}
                            </Typography>
                            <PodPhotos jobId={jobId} stopId={pickupStop.stopId} />
                          </CardContent>
                        </Card>
                      </>
                    )
                  }
                  return null
                })()}

                {/* Rider Information Section */}
                {selectedGroup.riderId && (
                  <>
                    <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
                      {t('riderInformation')}
                    </Typography>
                    <Card sx={styles.card}>
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <TwoWheeler /> {selectedGroup.riderName || 'Rider Name Not Available'}
                            </Typography>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Phone /> {selectedGroup.riderPhone || 'Phone number not available'}
                            </Typography>
                            {selectedGroup.deliveryWage && (
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#00b894', fontWeight: 'bold' }}>
                                💰 In-house Fee: ${selectedGroup.deliveryWage.toFixed(2)}
                              </Typography>
                            )}
                            {selectedGroup.lalamoveDeliveryFee && (
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#636e72' }}>
                                📊 Lalamove Fee: ${selectedGroup.lalamoveDeliveryFee.toFixed(2)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Fee Information Section (when no rider assigned) */}
                {!selectedGroup.riderId && (selectedGroup.deliveryWage || selectedGroup.lalamoveDeliveryFee) && (
                  <>
                    <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
                      {t('feeInformation')}
                    </Typography>
                    <Card sx={styles.card}>
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            {selectedGroup.deliveryWage && (
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#00b894', fontWeight: 'bold' }}>
                                💰 In-house Fee: ${selectedGroup.deliveryWage.toFixed(2)}
                              </Typography>
                            )}
                            {selectedGroup.lalamoveDeliveryFee && (
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#636e72' }}>
                                📊 Lalamove Fee: ${selectedGroup.lalamoveDeliveryFee.toFixed(2)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </>
                )}

                {selectedGroup?.restaurant?.location?.coordinates?.length >= 2 && (
                  <>
                    <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
                      {t('deliveryRoute')}
                    </Typography>
                    <DeliveryGroupMap 
                      restaurant={selectedGroup.restaurant}
                      stops={selectedGroup.stops}
                      riderLocation={riderLocationData}
                    />
                  </>
                )}

                <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
                  {t('Stops')}
                </Typography>

                {(selectedGroup.stops?.length ? selectedGroup.stops : []).map((stop, index) => (
                  <Card key={stop.stopId} sx={styles.orderCard}>
                    <CardContent>
                      <Box sx={styles.orderHeader}>
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                          {stop.type === 'PICKUP' ? <Store /> : <LocalShipping />}
                          {stop.type === 'PICKUP'
                            ? (stop.label || `Pickup ${index + 1}`)
                            : (stop.label || stop.orderId || `Dropoff ${index + 1}`)}
                        </Typography>
                        {stop.type !== 'PICKUP' && (
                          <Chip
                            label={stop.orders?.[0]?.orderStatus}
                            sx={{
                              ml: 2,
                              backgroundColor:
                                stop.orders?.[0]?.orderStatus === 'DELIVERED' ? '#4CAF50' : '#FF9800',
                              color: '#fff'
                            }}
                            size="small"
                          />
                        )}
                      </Box>
                      <Grid container spacing={1}>
                        {stop.type === 'PICKUP' ? (
                          <>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Store /> {stop.restaurantName || selectedGroup.restaurant?.name}
                              </Typography>
                              {stop.restaurantPhone && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Phone /> {stop.restaurantPhone}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                <LocationOn /> {stop.address}
                              </Typography>
                              {stop.details && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                                  <LocationCity /> {stop.details}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                              {stop.deliveryInstructions && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Info /> {stop.deliveryInstructions}
                                </Typography>
                              )}
                              {stop.totalPackages != null && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <ShoppingCart /> {stop.totalPackages} packages
                                </Typography>
                              )}
                              {stop.estimatedTime && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Schedule /> Est. {formatEstimatedTime(stop.estimatedTime, selectedGroup?.timezone)}
                                </Typography>
                              )}
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Person /> {stop.customerName || 'Customer'}
                              </Typography>
                              {stop.customerPhone && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Phone /> {stop.customerPhone}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                <LocationOn /> {stop.address}
                              </Typography>
                              {stop.details && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                                  <LocationCity /> {stop.details}
                                </Typography>
                              )}
                              {stop.neighbourhood && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                                  <LocationCity /> {stop.neighbourhood}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                              {stop.orderId && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <LocalShipping /> Order: {stop.orderId}
                                </Typography>
                              )}
                              {stop.deliveryInstructions && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Info /> {stop.deliveryInstructions}
                                </Typography>
                              )}
                              {(stop.itemCount != null || stop.orderAmount != null) && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <ShoppingCart /> {stop.itemCount != null ? `${stop.itemCount} items` : ''}
                                  {stop.itemCount != null && stop.orderAmount != null ? ' · ' : ''}
                                  {stop.orderAmount != null ? `$${Number(stop.orderAmount).toFixed(2)}` : ''}
                                </Typography>
                              )}
                              {stop.orderStatus && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Loop />Status: {stop.orderStatus}
                                </Typography>
                              )}
                              {stop.estimatedTime && (
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Schedule /> Est. {formatEstimatedTime(stop.estimatedTime, selectedGroup?.timezone)}
                                </Typography>
                              )}
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} md={4}>
                          {(stop.orders?.[0]?.orderStatus === 'DELIVERED') && stop.completedAt && (() => {
                            const completedDate = new Date(Number(stop.completedAt))
                            let dateText
                            if (isToday(completedDate)) {
                              dateText = `Today at ${format(completedDate, 'h:mm aaa')}`
                            } else if (isYesterday(completedDate)) {
                              dateText = `Yesterday at ${format(completedDate, 'h:mm aaa')}`
                            } else {
                              dateText = format(completedDate, 'd-MMM (eee) at h:mm aaa')
                            }
                            return (
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#4CAF50', fontWeight: 'medium', mb: 1 }}>
                                <CheckCircle sx={{ fontSize: '1rem', mr: 1 }} />
                                Completed: {dateText}
                              </Typography>
                            )
                          })()}
                          {stop.type === 'DROPOFF' && (() => {
                            const jobId = getJobId(selectedGroup._id)
                            if (jobId && stop.stopId) {
                              return <PodPhotos jobId={jobId} stopId={stop.stopId} orderId={stop.orderId} />
                            }
                            return null
                          })()}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </Paper>
        </Modal>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => !isPlacingOrder && setConfirmDialogOpen(false)}
        >
          <DialogTitle>Confirm Delivery Order</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to place this delivery order?
            </DialogContentText>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel id="delivery-order-mode-label">Order mode</InputLabel>
                <Select
                  labelId="delivery-order-mode-label"
                  value={createOrderPerPickup ? 'perPickup' : 'single'}
                  label="Order mode"
                  onChange={(e) => setCreateOrderPerPickup(e.target.value === 'perPickup')}
                  sx={{
                    color: '#000000',
                    '& .MuiSelect-select': {
                      color: '#000000'
                    }
                  }}
                >
                  <MenuItem value="single" sx={{ color: '#000000' }}>Single order for the whole job</MenuItem>
                  <MenuItem value="perPickup" sx={{ color: '#000000' }}>Create one order per pickup</MenuItem>
                </Select>
              </FormControl>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setConfirmDialogOpen(false)
                setCreateOrderPerPickup(false)
              }} 
              color="primary"
              disabled={isPlacingOrder}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handlePlaceDelivery(selectedGroup._id)} 
              color="primary" 
              variant="contained"
              disabled={isPlacingOrder}
            >
              {isPlacingOrder ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Rider Confirmation Dialog */}
        <Dialog
          open={assignRiderDialogOpen}
          onClose={() => setAssignRiderDialogOpen(false)}
        >
          <DialogTitle>Assign Rider</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to assign {pendingRiderName || 'this rider'} to this delivery group?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setAssignRiderDialogOpen(false)
                setPendingRiderId(null)
                setPendingRiderName(null)
              }} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmAssignRider} 
              color="primary" 
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unassign Rider Confirmation Dialog */}
        <Dialog
          open={unassignRiderDialogOpen}
          onClose={() => setUnassignRiderDialogOpen(false)}
        >
          <DialogTitle>Unassign Rider</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to unassign {selectedGroup?.riderName || 'the rider'} from this delivery group?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setUnassignRiderDialogOpen(false)} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmUnassignRider} 
              color="primary" 
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  )
}

export default withTranslation()(DeliveryGroups) 